
// Import React and necessary hooks
import React, { useEffect, useRef, useState } from "react";
// Import CSS for custom select styling
import "../../css/CustomSelect.css";
// Import arrow icon for dropdown
import { ReactComponent as ArrowDown } from "../../assets/icons/arrow_down.svg";


/**
 * CustomSelect component for rendering a styled dropdown select menu.
 *
 * Props:
 * - options: Array of option objects ({ label, value })
 * - value: Currently selected value
 * - onChange: Callback when selection changes
 * - name: Name attribute for the select
 * - placeholder: Placeholder text when no option is selected
 * - className: Additional CSS classes
 */
export default function CustomSelect({
  options = [], // Array of options
  value = "", // Selected value
  onChange, // Change handler
  name = "", // Name attribute
  placeholder = "Please select", // Placeholder text
  className = "", // Additional CSS classes
}) {
  // State to control dropdown open/close
  const [isOpen, setIsOpen] = useState(false);
  // Ref for detecting outside clicks
  const wrapperRef = useRef(null);

  // Find the selected option object
  const selectedOption = options.find((option) => option.value === value);

  // Effect to handle closing dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle selecting an option
  const handleSelect = (optionValue) => {
    if (onChange) {
      onChange({
        target: {
          name,
          value: optionValue,
        },
      });
    }
    setIsOpen(false);
  };

  return (
    <div ref={wrapperRef} className={`custom-select ${className}`}>
      {/* Trigger button for dropdown */}
      <button
        type="button"
        className={`custom-select-trigger ${isOpen ? "open" : ""}`}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="custom-select-text">
          {selectedOption?.label || placeholder}
        </span>
        <span className={`custom-select-arrow ${isOpen ? "open" : ""}`}>
          <ArrowDown />
        </span>
      </button>

      {/* Dropdown options */}
      {isOpen && (
        <div className="custom-select-dropdown">
          {options.map((option) => {
            const isSelected = option.value === value;

            return (
              <button
                key={option.value || option.label}
                type="button"
                className={`custom-select-option ${
                  isSelected ? "selected" : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
